/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  TextField,
  Box,
  Typography,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Divider,
  Alert,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

import moment from "moment";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import toast from "react-hot-toast";

import {
  createNewInvestment,
  editNewInvestment,
  deleteInvestment,
} from "../../actions/newInvestments";
import API from "../../Api/api";
import { nextInvestmentNumber } from "../../utils/investmentCode";

const columns = [
  { id: "status", label: "Status", minWidth: 110 },
  { id: "code", label: "Code", minWidth: 170 },
  { id: "investment_type", label: "Investment type", minWidth: 170 },
  { id: "regulator", label: "Regulator", minWidth: 150 },
  {
    id: "cost",
    label: "Cost",
    minWidth: 130,
    align: "right",
    format: (value) => Number(value || 0).toLocaleString("en-US"),
  },
  { id: "tenure", label: "Tenure", minWidth: 90, align: "right" },
  { id: "rate", label: "Rate (%)", minWidth: 90, align: "right" },
  { id: "year_days", label: "Year days", minWidth: 90, align: "right" },
  {
    id: "purchase_date",
    label: "Purchase date",
    minWidth: 130,
    align: "right",
  },
  {
    id: "maturity_date",
    label: "Maturity date",
    minWidth: 130,
    align: "right",
  },
];

const safeParse = (v) => {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};
const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function computeInvestmentDerived(form) {
  const cost = safeNum(form.cost);
  const ratePct = safeNum(form.rate);
  const tenure = safeNum(form.tenure);
  const yearDays = safeNum(form.year_days);
  const charges = safeNum(form.charges);

  const rate = ratePct / 100;

  const maturity_date =
    form.purchase_date && tenure > 0
      ? moment(form.purchase_date).add(tenure, "days").format("YYYY-MM-DD")
      : "";

  if (!cost || !rate || !tenure || !yearDays) {
    return {
      maturity_date,
      total_interest: 0,
      daily_interest: 0,
      investment_interest: 0,
    };
  }

  const investment_interest = cost * rate * (tenure / yearDays);
  const total_interest = investment_interest - charges;
  const daily_interest =
    tenure > 0 ? Number((total_interest / tenure).toFixed(8)) : 0;

  return { maturity_date, total_interest, daily_interest, investment_interest };
}

function makeInvestmentCode({ purchase_date, regCode, instCode, number }) {
  const year = purchase_date
    ? new Date(purchase_date).getFullYear()
    : new Date().getFullYear();
  return `SRB/${regCode}/${String(instCode || "").toLowerCase()}/${year}/${number}`;
}

// Asks the server for every investment so the number is current
async function fetchNextInvestmentNumber() {
  const { data } = await API.get("/api/v1/investment");

  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.investments)
        ? data.investments
        : null;

  if (!list)
    throw new Error("API response is not a list (array) of investments");

  return nextInvestmentNumber(list);
}

export default function Invest() {
  const dispatch = useDispatch();

  const investmentTypes = useSelector((state) => state.investmentTypes || []);
  const regulators = useSelector((state) => state.regulators || []);
  const institutions = useSelector((state) => state.institution || []);
  const newInvestmentData = useSelector((state) => state.newInvestment || []);

  const [newInvestment, setNewInvestment] = React.useState(newInvestmentData);
  React.useEffect(
    () => setNewInvestment(newInvestmentData),
    [newInvestmentData],
  );

  const user = React.useMemo(
    () => safeParse(secureLocalStorage.getItem("profile")),
    [],
  );

  const allowManipulate =
    user?.results?.portfolio_manager === "Administrator" ||
    user?.results?.portfolio_manager === "Read-Write";

  // paging
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // filters
  const [q, setQ] = React.useState("");
  const [filterType, setFilterType] = React.useState("");
  const [filterReg, setFilterReg] = React.useState("");

  // dialogs
  const [openCreate, setOpenCreate] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openDelete, setOpenDelete] = React.useState(false);

  const [selectedRow, setSelectedRow] = React.useState(null);

  // originals for edit stability
  const [originalInvestmentType, setOriginalInvestmentType] =
    React.useState("");
  const [originalCodeNumber, setOriginalCodeNumber] = React.useState("");

  // number generation
  const [codeNumber, setCodeNumber] = React.useState("");
  const [generating, setGenerating] = React.useState(false);

  const emptyForm = React.useMemo(
    () => ({
      cost: "", // store as string (NumericFormat value)
      rate: "",
      discount_rate: "",
      purchase_date: "",
      maturity_date: "",
      year_days: "",
      charges: "",
      investment_type: "",
      institution: "",
      regulator: "",
      code: "",
      uploaded_by:
        `${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`.trim(),
      tenure: "",
      daily_interest: "",
      total_interest: "",
      rate_type: "",
    }),
    [user],
  );

  const [investmentData, setInvestmentData] = React.useState(emptyForm);

  const derived = React.useMemo(
    () => computeInvestmentDerived(investmentData),
    [investmentData],
  );

  const getRegCode = React.useCallback(
    (regName) => regulators.find((r) => r.regulator === regName)?.code || "",
    [regulators],
  );

  const getInstTypeCode = React.useCallback(
    (type) =>
      investmentTypes.find((t) => t.investment_type === type)?.code || "",
    [investmentTypes],
  );

  // keep original code number on edit (unless type changes)
  React.useEffect(() => {
    if (!investmentData.investment_type) return;

    const isEdit = openEdit && !!selectedRow;
    const typeChanged =
      isEdit && investmentData.investment_type !== originalInvestmentType;

    if (isEdit && !typeChanged) {
      setCodeNumber(originalCodeNumber || "");
    }
  }, [
    investmentData.investment_type,
    openEdit,
    selectedRow,
    originalInvestmentType,
    originalCodeNumber,
  ]);

  const investmentCodePreview = React.useMemo(() => {
    const regCode = getRegCode(investmentData.regulator);
    const instCode = getInstTypeCode(investmentData.investment_type);

    if (!investmentData.purchase_date || !regCode || !instCode || !codeNumber)
      return "";
    return makeInvestmentCode({
      purchase_date: investmentData.purchase_date,
      regCode,
      instCode,
      number: codeNumber,
    });
  }, [
    investmentData.purchase_date,
    investmentData.regulator,
    investmentData.investment_type,
    codeNumber,
    getRegCode,
    getInstTypeCode,
  ]);

  const canSubmit = React.useMemo(() => {
    const required =
      investmentData.cost &&
      investmentData.rate &&
      investmentData.purchase_date &&
      investmentData.year_days &&
      investmentData.investment_type &&
      investmentData.institution &&
      investmentData.regulator &&
      investmentData.tenure &&
      investmentData.rate_type &&
      investmentCodePreview;

    return Boolean(required) && !generating;
  }, [investmentData, investmentCodePreview, generating]);

  const validate = () => {
    if (!allowManipulate) {
      toast.error("You don't have permission to perform this action.");
      return false;
    }

    if (
      investmentData.purchase_date &&
      moment(investmentData.purchase_date).isAfter(moment(), "day")
    ) {
      toast.error("Purchase date cannot be in the future.");
      return false;
    }

    if (safeNum(investmentData.rate) <= 0) {
      toast.error("Rate must be greater than zero.");
      return false;
    }

    if (safeNum(investmentData.tenure) <= 0) {
      toast.error("Tenure must be greater than zero.");
      return false;
    }

    if (![364, 365, 366].includes(safeNum(investmentData.year_days))) {
      toast.error("Year days must be 364, 365, or 366.");
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setInvestmentData(emptyForm);
    setSelectedRow(null);
    setOriginalInvestmentType("");
    setOriginalCodeNumber("");
    setCodeNumber("");
  };

  const refreshCodeNumber = async () => {
    try {
      setGenerating(true);
      const next = await fetchNextInvestmentNumber();
      setCodeNumber(next);
    } catch (e) {
      toast.error(e?.message || "Failed to generate code number");
    } finally {
      setGenerating(false);
    }
  };

  const openCreateDialog = async () => {
    resetForm();
    setOpenCreate(true);
    await refreshCodeNumber();
  };

  const openEditDialog = (row) => {
    setSelectedRow(row);

    const originalType = row?.investment_type || "";
    setOriginalInvestmentType(originalType);

    const lastCode =
      String(row?.code || "")
        .split("/")
        .pop() || "";
    setOriginalCodeNumber(lastCode);

    setInvestmentData({
      cost: String(row?.cost ?? ""), // keep as string
      rate: String(row?.rate ?? ""),
      discount_rate: String(row?.discount_rate ?? ""),
      purchase_date: row?.purchase_date
        ? moment(row.purchase_date).format("YYYY-MM-DD")
        : "",
      maturity_date: row?.maturity_date
        ? moment(row.maturity_date).format("YYYY-MM-DD")
        : "",
      year_days: row?.year_days ? Number(row.year_days) : "",
      charges: String(row?.charges ?? ""),
      investment_type: row?.investment_type ?? "",
      institution: row?.institution ?? "",
      regulator: row?.regulator ?? "",
      code: row?.code ?? "",
      uploaded_by:
        row?.uploaded_by ||
        `${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`.trim(),
      tenure: String(row?.tenure ?? ""),
      daily_interest: String(row?.daily_interest ?? ""),
      total_interest: String(row?.total_interest ?? ""),
      rate_type: row?.rate_type ?? "",
      _id: row?._id,
    });

    setCodeNumber(lastCode);
    setOpenEdit(true);
  };

  const openDeleteDialog = (row) => {
    setSelectedRow(row);
    setOpenDelete(true);
  };

  const buildPayload = (codeOverride) => ({
    ...investmentData,
    maturity_date: derived.maturity_date,
    total_interest: derived.total_interest,
    daily_interest: derived.daily_interest,
    code: codeOverride || investmentCodePreview,
    uploaded_by:
      `${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`.trim(),
  });

  const handleCreate = async () => {
    if (!validate()) return;

    try {
      setGenerating(true);

      // fetch fresh number on submit (your safety step)
      const fresh = await fetchNextInvestmentNumber();
      setCodeNumber(fresh);

      const regCode = getRegCode(investmentData.regulator);
      const instCode = getInstTypeCode(investmentData.investment_type);

      if (!investmentData.purchase_date || !regCode || !instCode) {
        toast.error(
          "Please fill purchase date, regulator and investment type.",
        );
        return;
      }

      const freshCode = makeInvestmentCode({
        purchase_date: investmentData.purchase_date,
        regCode,
        instCode,
        number: fresh,
      });

      await dispatch(createNewInvestment(buildPayload(freshCode)));
      setOpenCreate(false);
      resetForm();
    } catch (e) {
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdate = async () => {
    if (!validate()) return;

    const id = selectedRow?._id || investmentData?._id;
    if (!id) {
      toast.error("Missing investment id.");
      return;
    }

    try {
      setGenerating(true);
      await dispatch(editNewInvestment(id, buildPayload()));
      setOpenEdit(false);
      resetForm();
    } catch (e) {
      // the action has already shown the error
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!allowManipulate) {
      toast.error("You don't have permission to perform this action.");
      return;
    }
    if (!selectedRow?._id) {
      toast.error("Missing investment id.");
      return;
    }

    try {
      setGenerating(true);
      await dispatch(deleteInvestment(selectedRow._id));
      setOpenDelete(false);
      setSelectedRow(null);
    } catch (e) {
      // the action has already shown the error
    } finally {
      setGenerating(false);
    }
  };

  const rows = React.useMemo(() => {
    return (newInvestment || []).map((d) => ({
      id: d._id,
      code: d.code,
      status: d.approval_status === "returned" ? "returned" : "pending",
      investment_type: d.investment_type,
      regulator: d.regulator,
      cost: d.cost,
      tenure: d.tenure,
      rate: d.rate,
      year_days: d.year_days,
      purchase_date: d.purchase_date
        ? moment(d.purchase_date).format("DD-MM-YYYY")
        : "",
      maturity_date: d.maturity_date
        ? moment(d.maturity_date).format("DD-MM-YYYY")
        : "",
      _raw: d,
    }));
  }, [newInvestment]);

  // A new search or filter starts from the first page
  React.useEffect(() => setPage(0), [q, filterType, filterReg]);

  const filteredRows = React.useMemo(() => {
    const needle = String(q || "")
      .trim()
      .toLowerCase();

    return rows.filter((r) => {
      const matchesQ =
        !needle ||
        String(r.code || "")
          .toLowerCase()
          .includes(needle) ||
        String(r.investment_type || "")
          .toLowerCase()
          .includes(needle) ||
        String(r.regulator || "")
          .toLowerCase()
          .includes(needle);

      const matchesType = !filterType || r.investment_type === filterType;
      const matchesReg = !filterReg || r.regulator === filterReg;

      return matchesQ && matchesType && matchesReg;
    });
  }, [rows, q, filterType, filterReg]);

  return (
    <div>
      <Paper sx={{ width: "100%", borderRadius: 3, overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                Investments Submitted for Approval
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                add, update, and edit investment details before approval.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip size="small" label={`${filteredRows.length} records`} />
              <Button
                size="small"
                disabled={!allowManipulate}
                onClick={openCreateDialog}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Add Investment
              </Button>
            </Stack>
          </Stack>

          {!allowManipulate ? (
            <Typography
              sx={{ mt: 1, color: "error.main", fontStyle: "italic" }}
            >
              You don’t have permission to add investment.
            </Typography>
          ) : null}

          {/* Search + filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{ mt: 2 }}
          >
            <TextField
              size="small"
              fullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code, type, regulator..."
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mr: 1,
                      opacity: 0.7,
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </Box>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {investmentTypes.map((t) => (
                  <MenuItem key={t._id} value={t.investment_type}>
                    {t.investment_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Regulator</InputLabel>
              <Select
                label="Regulator"
                value={filterReg}
                onChange={(e) => setFilterReg(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {regulators.map((r) => (
                  <MenuItem key={r._id} value={r.regulator}>
                    {r.regulator}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 720 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      minWidth: column.minWidth,
                      fontWeight: 900,
                      backgroundColor: "#f7f7fb",
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: 120,
                    fontWeight: 900,
                    backgroundColor: "#f7f7fb",
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.id === "status" ? (
                            value === "returned" ? (
                              <Tooltip
                                title={`Returned by ${row._raw.returned_by || "the approver"}: ${row._raw.returned_reason || ""}. Edit it to send it back for approval.`}
                              >
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="Returned"
                                />
                              </Tooltip>
                            ) : (
                              <Chip size="small" label="Awaiting approval" />
                            )
                          ) : column.format && typeof value === "number" ? (
                            column.format(value)
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!allowManipulate || generating}
                              onClick={() => openEditDialog(row._raw)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!allowManipulate || generating}
                              onClick={() => openDeleteDialog(row._raw)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
          }}
        />
      </Paper>

      {/* =========================
          CREATE DIALOG (FORM)
         ========================= */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Add Investment
          <Box
            sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}
          >
            <Chip
              size="small"
              label={generating ? "Generating..." : `No: ${codeNumber || "—"}`}
              color={generating ? "warning" : "default"}
              variant={generating ? "filled" : "outlined"}
            />
            <Tooltip title="Refresh number">
              <span>
                <IconButton
                  size="small"
                  onClick={refreshCodeNumber}
                  disabled={generating}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={() => setOpenCreate(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 900 }}>Preview</Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                Investment code is generated from server count.
              </Typography>

              <Divider sx={{ my: 1.2 }} />

              <TextField
                label="Investment Code (auto)"
                size="small"
                fullWidth
                disabled
                value={
                  investmentCodePreview || (generating ? "Generating..." : "")
                }
              />

              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Total interest: ${safeNum(derived.total_interest).toLocaleString()}`}
                />
                <Chip
                  size="small"
                  label={`Daily interest: ${safeNum(derived.daily_interest)}`}
                />
                <Chip
                  size="small"
                  label={`Maturity: ${derived.maturity_date || "—"}`}
                />
              </Stack>
            </Paper>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Basic</Typography>

                <NumericFormat
                  customInput={TextField}
                  label="Cost"
                  size="small"
                  required
                  fullWidth
                  sx={{ mb: 1.2 }}
                  thousandSeparator=","
                  decimalScale={2}
                  allowNegative={false}
                  value={investmentData.cost}
                  // ✅ critical: keep as string, and use functional update
                  onValueChange={(values) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      cost: values.value,
                    }))
                  }
                />

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="number"
                  size="small"
                  label="Rate (%)"
                  required
                  value={investmentData.rate}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      rate: e.target.value,
                    }))
                  }
                />

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Rate Type</InputLabel>
                  <Select
                    label="Rate Type"
                    value={investmentData.rate_type}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        rate_type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="discount rate">discount rate</MenuItem>
                    <MenuItem value="interest rate">interest rate</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="date"
                  size="small"
                  label="Purchase Date"
                  required
                  value={investmentData.purchase_date}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      purchase_date: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  sx={{ mb: 0 }}
                  type="number"
                  size="small"
                  label="Tenure (days)"
                  required
                  value={investmentData.tenure}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      tenure: e.target.value,
                    }))
                  }
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Classification
                </Typography>

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Year days</InputLabel>
                  <Select
                    label="Year days"
                    value={investmentData.year_days}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        year_days: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={364}>364</MenuItem>
                    <MenuItem value={365}>365</MenuItem>
                    <MenuItem value={366}>366</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="number"
                  size="small"
                  label="Charges"
                  required
                  value={investmentData.charges}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      charges: e.target.value,
                    }))
                  }
                />

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Investment type</InputLabel>
                  <Select
                    label="Investment type"
                    value={investmentData.investment_type}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        investment_type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {investmentTypes.map((data) => (
                      <MenuItem key={data._id} value={data.investment_type}>
                        {data.investment_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Institution</InputLabel>
                  <Select
                    label="Institution"
                    value={investmentData.institution}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        institution: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {institutions.map((data) => (
                      <MenuItem key={data._id} value={data.institution}>
                        {data.institution}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={{ mb: 0 }}>
                  <InputLabel required>Regulator</InputLabel>
                  <Select
                    label="Regulator"
                    value={investmentData.regulator}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        regulator: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {regulators.map((data) => (
                      <MenuItem key={data._id} value={data.regulator}>
                        {data.regulator}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setOpenCreate(false);
              resetForm();
            }}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2 }}
            disabled={generating}
          >
            Cancel
          </Button>

          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ textTransform: "none", borderRadius: 2 }}
            disabled={!canSubmit || generating}
          >
            {generating ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =========================
          EDIT DIALOG (FORM)
         ========================= */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Update Investment
          <Box
            sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}
          >
            <Chip
              size="small"
              label={`No: ${codeNumber || "—"}`}
              variant="outlined"
            />
            <IconButton onClick={() => setOpenEdit(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {selectedRow?.approval_status === "returned" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Returned by {selectedRow.returned_by || "the approver"}:{" "}
              {selectedRow.returned_reason}. Saving your changes sends it back
              for approval.
            </Alert>
          )}
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 900 }}>Preview</Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                Edit keeps the original code number (unless you manually refresh
                on create).
              </Typography>

              <Divider sx={{ my: 1.2 }} />

              <TextField
                label="Investment Code (auto)"
                size="small"
                fullWidth
                disabled
                value={investmentCodePreview || ""}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Total interest: ${safeNum(derived.total_interest).toLocaleString()}`}
                />
                <Chip
                  size="small"
                  label={`Daily interest: ${safeNum(derived.daily_interest)}`}
                />
                <Chip
                  size="small"
                  label={`Maturity: ${derived.maturity_date || "—"}`}
                />
              </Stack>
            </Paper>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Basic</Typography>

                <NumericFormat
                  customInput={TextField}
                  label="Cost"
                  size="small"
                  required
                  fullWidth
                  sx={{ mb: 1.2 }}
                  thousandSeparator=","
                  decimalScale={2}
                  allowNegative={false}
                  value={investmentData.cost}
                  onValueChange={(values) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      cost: values.value,
                    }))
                  }
                />

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="number"
                  size="small"
                  label="Rate (%)"
                  required
                  value={investmentData.rate}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      rate: e.target.value,
                    }))
                  }
                />

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Rate Type</InputLabel>
                  <Select
                    label="Rate Type"
                    value={investmentData.rate_type}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        rate_type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="discount rate">discount rate</MenuItem>
                    <MenuItem value="interest rate">interest rate</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="date"
                  size="small"
                  label="Purchase Date"
                  required
                  value={investmentData.purchase_date}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      purchase_date: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  sx={{ mb: 0 }}
                  type="number"
                  size="small"
                  label="Tenure (days)"
                  required
                  value={investmentData.tenure}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      tenure: e.target.value,
                    }))
                  }
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Classification
                </Typography>

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Year days</InputLabel>
                  <Select
                    label="Year days"
                    value={investmentData.year_days}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        year_days: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={364}>364</MenuItem>
                    <MenuItem value={365}>365</MenuItem>
                    <MenuItem value={366}>366</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  sx={{ mb: 1.2 }}
                  type="number"
                  size="small"
                  label="Charges"
                  required
                  value={investmentData.charges}
                  onChange={(e) =>
                    setInvestmentData((prev) => ({
                      ...prev,
                      charges: e.target.value,
                    }))
                  }
                />

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Investment type</InputLabel>
                  <Select
                    label="Investment type"
                    value={investmentData.investment_type}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        investment_type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {investmentTypes.map((data) => (
                      <MenuItem key={data._id} value={data.investment_type}>
                        {data.investment_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={{ mb: 1.2 }}>
                  <InputLabel required>Institution</InputLabel>
                  <Select
                    label="Institution"
                    value={investmentData.institution}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        institution: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {institutions.map((data) => (
                      <MenuItem key={data._id} value={data.institution}>
                        {data.institution}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={{ mb: 0 }}>
                  <InputLabel required>Regulator</InputLabel>
                  <Select
                    label="Regulator"
                    value={investmentData.regulator}
                    onChange={(e) =>
                      setInvestmentData((prev) => ({
                        ...prev,
                        regulator: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {regulators.map((data) => (
                      <MenuItem key={data._id} value={data.regulator}>
                        {data.regulator}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2 }}
            disabled={generating}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ textTransform: "none", borderRadius: 2 }}
            disabled={!allowManipulate || generating}
          >
            {generating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Investment</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <WarningAmberIcon sx={{ color: "#D32F2F" }} />
            <Typography sx={{ fontWeight: 800 }}>
              This action cannot be undone.
            </Typography>
          </Stack>

          <Typography sx={{ fontSize: 12, fontWeight: 900, opacity: 0.7 }}>
            Code
          </Typography>
          <Typography sx={{ fontWeight: 900 }}>
            {selectedRow?.code || "—"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDelete(false)}
            sx={{ textTransform: "none" }}
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            disabled={!allowManipulate || generating}
          >
            {generating ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
